// ============================================================
// usePanelUrlSync — サイドパネルの開閉状態を URL クエリと双方向同期する共通フック
// ============================================================
//
// 目的: パネルで表示中のエンティティ（予約・車両）を URL に反映し、
//   - URL をコピペ共有 → 開いた人も同じパネルが開く（ディープリンク）
//   - ブラウザバックでパネルが閉じる
// を実現する。仕様: md/master_md/feature-sidepanel.md「URL連動」
//
// 設計原則:
//   - 「URL で指せる = 誰が開いても同じものが見える」エンティティのみ対象。
//     一時モーダル（価格編集・確認ダイアログ等）や共有不可能な新規ドラフト
//     （tmp_ ID）は URL 化しない（getUrlValue が null を返せば対象外）。
//   - 復元は「ロード済みの完全データ」からのみ行う（resolve に渡すリストの責務）。
//     部分データで setEditing すると 2026-03-28 のフィールド空上書き事故と
//     同型になるため、resolve は必ず完全なオブジェクトを返すこと。
//   - ヒストリ操作: 開く=push（バックで閉じられる）/ パネル内切替=replace /
//     閉じる=replace でクエリ除去（共有URLから直接開いた人がバックで
//     空ページへ飛ばされないように back() は使わない）。
//
// 同期の解決規則（無限ループ防止）:
//   毎エフェクトで「state が前回から変わったか」を ref で判定し、
//   変わっていれば state → URL、変わっていなければ URL → state の一方向のみ実行。
//   初回マウントは「URL が真実」（ディープリンク復元）。
//   判定ロジックの本体は panelUrlSyncCore.ts（純関数・テスト済み）。

import { useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { decideSyncAction } from './panelUrlSyncCore';

export { decideSyncAction, findReservationByParam, findVehicleByParam } from './panelUrlSyncCore';
export type { SyncAction } from './panelUrlSyncCore';

export interface PanelUrlSyncOptions<T> {
  /** URL クエリキー（例: 'res' / 'vehicle'）。全ページで同じキーを使うこと */
  paramKey: string;
  /** 現在パネルに表示中のオブジェクト（null = 閉） */
  current: T | null;
  /** URL に載せる値。null を返すと URL 管理対象外（例: tmp_ 新規ドラフト） */
  getUrlValue: (obj: T) => string | null;
  /** URL 値 → オブジェクト復元。ロード済みデータから検索し、無ければ null */
  resolve: (value: string) => T | null;
  /** データロード完了か。false の間は URL → state 復元を保留する */
  isReady: boolean;
  /** URL から復元したオブジェクトでパネルを開く */
  onOpen: (obj: T) => void;
  /** URL からクエリが消えた（ブラウザバック等）のでパネルを閉じる */
  onClose: () => void;
  /** URL の値がデータ中に見つからない（削除済み等）。クエリは自動除去される */
  onNotFound?: (value: string) => void;
}

export function usePanelUrlSync<T>(opts: PanelUrlSyncOptions<T>): void {
  const { paramKey, current, isReady } = opts;
  const [searchParams, setSearchParams] = useSearchParams();

  // コールバック類は ref 経由で参照し、呼び出し側に useCallback を強制しない
  const optsRef = useRef(opts);
  optsRef.current = opts;

  // 前回エフェクト実行時の state 側の値。undefined = 初回（URL を真実とする）
  const lastStateValRef = useRef<string | null | undefined>(undefined);

  const stateVal = current ? optsRef.current.getUrlValue(current) : null;
  const urlVal = searchParams.get(paramKey);

  useEffect(() => {
    if (!isReady) return;
    const o = optsRef.current;

    const action = decideSyncAction({
      isFirstRun: lastStateValRef.current === undefined,
      stateVal,
      lastStateVal: lastStateValRef.current ?? null,
      urlVal,
    });
    lastStateValRef.current = stateVal;

    switch (action.type) {
      case 'write-url':
        setSearchParams(prev => {
          const p = new URLSearchParams(prev);
          if (action.value !== null) p.set(paramKey, action.value);
          else p.delete(paramKey);
          return p;
        }, { replace: !action.push });
        break;
      case 'open-from-url': {
        const resolved = o.resolve(action.value);
        if (resolved !== null) {
          o.onOpen(resolved);
        } else {
          o.onNotFound?.(action.value);
          setSearchParams(prev => {
            const p = new URLSearchParams(prev);
            p.delete(paramKey);
            return p;
          }, { replace: true });
        }
        break;
      }
      case 'close-from-url':
        o.onClose();
        break;
      case 'none':
        break;
    }
    // 依存は意図的に絞っている: コールバック類は optsRef 経由（安定参照不要のため）
  }, [searchParams, stateVal, isReady, paramKey]);
}
